/**
 * Razorpay Payment Utility
 * Provides reusable functions for Razorpay SDK loading and payment initialization
 */

/**
 * Load Razorpay SDK with retry logic
 * Checks if Razorpay SDK is already loaded, otherwise waits for it to load
 * @param {number} maxAttempts - Maximum number of attempts to check for SDK (default: 10)
 * @param {number} checkInterval - Interval between checks in milliseconds (default: 200)
 * @returns {Promise<boolean>} - Returns true if SDK is loaded, false otherwise
 */
export const loadRazorpaySDK = (maxAttempts = 10, checkInterval = 200) => {
  return new Promise((resolve) => {
    // If SDK is already loaded, resolve immediately
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    // If not loaded, try to wait a bit and check again
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.Razorpay) {
        clearInterval(interval);
        resolve(true);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        resolve(false);
      }
    }, checkInterval);
  });
};

/**
 * Initialize Razorpay payment
 * Handles both web and hybrid app (React Native WebView) scenarios
 * @param {Object} options - Razorpay payment options
 * @param {Function} onError - Error callback function
 * @returns {Promise<void>}
 */
export const initializeRazorpayPayment = async (options, onError) => {
  try {
    // Check for hybrid app payment trigger (React Native WebView)
    if (window.triggerRazorpayPayment) {
      // Remove callback_url for hybrid app as it's handled differently
      const hybridOptions = { ...options };
      delete hybridOptions.callback_url;
      window.triggerRazorpayPayment(hybridOptions);
      return;
    }

    // Load Razorpay SDK for web
    const isSDKLoaded = await loadRazorpaySDK();

    if (!isSDKLoaded) {
      const errorMessage =
        'Razorpay SDK could not be loaded. Please check your internet connection and try again.';
      if (onError) {
        onError(new Error(errorMessage));
      } else {
        throw new Error(errorMessage);
      }
      return;
    }

    // Initialize and open Razorpay checkout
    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
};
